import { useReservationModalTranslation } from "../reservations/reservationModalI18n";
/**
 * Palantir PMS - Software para la gestión de la industria hotelera
 * Palantir PMS - Software for hotel industry management
 * Autor / Author: Alberto Castro Ull
 * Fecha de creación / Created on: 31/07/2025
 * Todos los derechos reservados / All rights reserved.
 * Este software es propiedad de Alberto Castro Ull.
 * This software is the property of Alberto Castro Ull.
 * Su uso está restringido a clientes con licencia emitida por Markulia Group, S.L.
 * Use is restricted to clients licensed by Markulia Group, S.L.
 */

import React, { useEffect, useState } from "react";
import { useCurrencyDisplay } from "../currency/CurrencyDisplayContext";
import {
  currencyAmountStep,
  getCurrencyMinorUnits,
} from "../currency/currencyPrecision";
import {
  getActiveGroupRateReservationIds,
  getGroupRateTargetReservations,
  isCancelledGroupRateReservation,
  MAX_GROUP_RATE_AMOUNT,
  isValidGroupRateAmount,
} from "../reservations/groupDraftUiContracts";

const GroupSpecialRate = ({
  show,
  onClose,
  onApply,
  groupReservations = [],
  baseCurrency = "EUR",
}) => {
  const { t: tr, locale } = useReservationModalTranslation();
  const { currencyCatalog = [] } = useCurrencyDisplay();
  const [rateName, setRateName] = useState("");
  const [rateAmount, setRateAmount] = useState("");
  const [scope, setScope] = useState("group"); // "group" or "per_reservation"
  const [priceType, setPriceType] = useState("total"); // "total" or "per_night"
  const [selectedIds, setSelectedIds] = useState(() =>
    getActiveGroupRateReservationIds(groupReservations)
  );
  const [notes, setNotes] = useState("");
  const activeReservationIds = getActiveGroupRateReservationIds(
    groupReservations
  );
  const activeReservationIdSet = new Set(activeReservationIds);
  const amountMinorUnits = getCurrencyMinorUnits(baseCurrency, currencyCatalog);

  useEffect(() => {
    if (!show) return;
    const currentActiveIds = getActiveGroupRateReservationIds(
      groupReservations
    );
    const currentActiveIdSet = new Set(currentActiveIds);
    setSelectedIds((previousIds) =>
      scope === "group"
        ? currentActiveIds
        : previousIds.filter((id) => currentActiveIdSet.has(Number(id)))
    );
  }, [show, groupReservations, scope]);

  const toggleReservation = (id) => {
    const normalizedId = Number(id);
    if (!activeReservationIdSet.has(normalizedId)) return;
    setSelectedIds(prev =>
      prev.includes(normalizedId)
        ? prev.filter(rid => rid !== normalizedId)
        : [...prev, normalizedId]
    );
  };

  if (!show) return null;

  return (
    <div
      className="rgv-linkedOverlay rgv-specialRateOverlay position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
      style={{
        background: "rgba(0,0,0,0.65)",
        zIndex: 9999,
        left: 0,
        top: 0
      }}
    >
      <div
        className="rgv-linkedModal rgv-specialRateModal bg-dark text-light rounded shadow-lg p-4"
        style={{ minWidth: 440, maxWidth: 560, width: "100%" }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="fw-bold mb-0 text-warning">{tr("Group Special Rate")}</h5>
          <button className="btn btn-sm btn-outline-light" onClick={onClose}>{tr("Close")}</button>
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">{tr("Rate name")}</label>
          <input
            className="form-control"
            value={rateName}
            onChange={e => setRateName(e.target.value)}
            placeholder={tr("Name the group rate")}
          />
        </div>

        <div className="row align-items-end g-2 mb-3">
          {/* Price column */}
          <div className="col-4">
            <label className="form-label fw-bold">{tr("Price (base")} {baseCurrency})</label>
            <input
              className="form-control"
              type="number"
              min="0"
              max={MAX_GROUP_RATE_AMOUNT}
              step={currencyAmountStep(baseCurrency, currencyCatalog)}
              inputMode={amountMinorUnits === 0 ? "numeric" : "decimal"}
              value={rateAmount}
              onChange={e => setRateAmount(e.target.value)}
              placeholder={amountMinorUnits === 0 ? tr("E.g. 230") : tr("E.g. 230.00")}
            />
          </div>
          {/* New selector: Total or Per night */}
          <div className="col-4">
            <label className="form-label fw-bold">{tr("Price type")}</label>
            <select
              className="form-select"
              value={priceType}
              onChange={e => setPriceType(e.target.value)}
            >
              <option value="total">{tr("Total")}</option>
              <option value="per_night">{tr("Per night")}</option>
            </select>
          </div>
          {/* Scope column */}
          <div className="col-4">
            <label className="form-label fw-bold">{tr("Apply to")}</label>
            <select
              className="form-select"
              value={scope}
              onChange={e => {
                setScope(e.target.value);
                if (e.target.value === "group") {
                  setSelectedIds(activeReservationIds);
                } else {
                  setSelectedIds([]);
                }
              }}
            >
              <option value="group">{tr("Entire group")}</option>
              <option value="per_reservation">{tr("Selected reservations")}</option>
            </select>
          </div>
        </div>


        {/* Only show if scope is per_reservation */}
        {scope === "per_reservation" && (
            <div className="rgv-linkedSection mb-3 border rounded p-2" style={{ background: "#1c1c1c" }}>
                <div className="d-flex align-items-center mb-2 gap-2">
                <label className="form-label fw-bold mb-0" style={{ flex: 1 }}> {tr("Select reservations")} </label>
                <button
                    type="button"
                    className="btn btn-sm btn-outline-light"
                    style={{ fontSize: "0.8rem" }}
                    onClick={() => setSelectedIds(activeReservationIds)}
                > {tr("All")} </button>
                <button
                    type="button"
                    className="btn btn-sm btn-outline-light"
                    style={{ fontSize: "0.8rem" }}
                    onClick={() => setSelectedIds([])}
                > {tr("None")} </button>
                </div>
                {groupReservations.map(res => {
                  const reservationId = Number(res.id);
                  const cancelled = isCancelledGroupRateReservation(res);
                  return (
                <div key={res.id} className="form-check mb-1">
                    <input
                    type="checkbox"
                    className="form-check-input"
                    checked={!cancelled && selectedIds.includes(reservationId)}
                    disabled={cancelled}
                    id={`select-res-${res.id}`}
                    onChange={() => toggleReservation(reservationId)}
                    />
                    <label className="form-check-label" htmlFor={`select-res-${res.id}`}>
                    {res.locator || tr("Room {number}", { number: res.group_index + 1 })} - {res.room_category_name}
                    {cancelled ? tr(" (Cancelled — unavailable)") : ""}
                    </label>
                </div>
                  );
                })}
                {selectedIds.length === 0 && (
                <div className="text-warning small mt-1">{tr("Select at least one reservation.")}</div>
                )}
            </div>
            )}


        <div className="mb-3">
          <label className="form-label fw-bold">{tr("Notes (optional)")}</label>
          <textarea
            className="form-control"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={2}
            placeholder={tr("Justification, reason or conditions…")}
          />
        </div>

        <div className="rgv-linkedActions d-flex justify-content-end gap-2 mt-3">
          <button className="btn btn-secondary" onClick={onClose}> {tr("Cancel")} </button>
          <button
            className="btn btn-primary fw-bold"
            onClick={() => {
              const targetReservationIds = getGroupRateTargetReservations({
                reservations: groupReservations,
                scope,
                selectedReservationIds: selectedIds,
              }).map((reservation) => Number(reservation.id));
              if (!rateName.trim()) {
                alert(tr("You must enter a name for the rate."));
                return;
              }
              if (!isValidGroupRateAmount(rateAmount, amountMinorUnits)) {
                alert(
                  amountMinorUnits === 0
                    ? tr("Enter a price between 0 and {max} using whole units.", { max: MAX_GROUP_RATE_AMOUNT.toLocaleString(locale) })
                    : tr("Enter a price between 0 and {max} with no more than {decimals} decimal places.", {
                        max: MAX_GROUP_RATE_AMOUNT.toLocaleString(locale),
                        decimals: amountMinorUnits,
                      })
                );
                return;
              }
              if (targetReservationIds.length === 0) {
                alert(tr("Select at least one reservation."));
                return;
              }
              onApply({
                rateName: rateName.trim(),
                priceType,
                rateAmount: parseFloat(rateAmount),
                scope, 
                notes: notes.trim(),
                selectedReservationIds: targetReservationIds,
              });
            }}
          > {tr("Apply special rate")} </button>
        </div>
      </div>
    </div>
  );
};

export default GroupSpecialRate;
